import { isValidElement, useCallback, useId, useRef, useState } from "react";

import { Duration } from "~/constants";

import { Icon } from "../icon/Icon";
import { Text } from "../text/Text";
import { When } from "../when/When";

import type { AccordionProps } from "./Accordion";

export interface AccordionItemProps {
  children?: React.ReactNode;
  defaultExpanded?: boolean;
  label: React.ReactNode;
  variant?: AccordionProps["variant"];
  onToggle?: (open: boolean) => void;
}

export function AccordionItem({
  children,
  defaultExpanded,
  label,
  variant = "plus-minus",
  onToggle,
}: AccordionItemProps) {
  const buttonId = useId();
  const panelId = useId();

  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const animateOpen = useCallback(() => {
    if (!panelRef.current) return;

    panelRef.current.hidden = false;

    panelRef.current
      .animate(
        [
          {
            opacity: 0,
            height: "0px",
          },
          {
            opacity: 1,
            height: `${contentRef.current?.getBoundingClientRect().height}px`,
          },
        ],
        {
          duration: Duration.Short,
          easing: "ease-in-out",
        },
      )
      .finished.then((animation) => {
        animation.commitStyles();
        animation.cancel();
      });
  }, [contentRef]);

  const animateClosed = useCallback(() => {
    if (!panelRef.current) return;

    panelRef.current
      .animate(
        [
          {
            opacity: 1,
            height: `${contentRef.current?.getBoundingClientRect().height}px`,
          },
          {
            opacity: 0,
            height: "0px",
          },
        ],
        {
          duration: Duration.Short,
          easing: "ease-in-out",
        },
      )
      .finished.then((animation) => {
        animation.commitStyles();
        animation.cancel();

        requestAnimationFrame(() => {
          if (panelRef.current) {
            panelRef.current.hidden = true;
          }
        });
      });
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      setIsExpanded((value) => {
        const newValue = !value;

        if (newValue) {
          animateOpen();
        } else {
          animateClosed();
        }

        onToggle?.(newValue);

        return newValue;
      });
    },
    [animateClosed, animateOpen, onToggle],
  );

  const renderLabel = useCallback(() => {
    if (isValidElement(label)) {
      return label;
    }

    return <Text weight="semibold">{String(label)}</Text>;
  }, [label]);

  return (
    <div className="AccordionItem">
      <button
        className="AccordionItem-button"
        id={buttonId}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        onClick={handleClick}
      >
        {renderLabel()}

        <div className="AccordionItem-button-marker">
          <When condition={variant === "chevron"}>
            <Icon icon="angle-down" />
          </When>

          <When condition={variant === "plus-minus"}>
            {isExpanded ? (
              <Icon icon="minus" variant="solid" />
            ) : (
              <Icon icon="plus" variant="solid" />
            )}
          </When>
        </div>
      </button>

      <div
        ref={panelRef}
        className="AccordionItem-panel"
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!defaultExpanded}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    </div>
  );
}
